
import React from 'react';
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { CheckCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { EnterpriseCallout } from "@/components/pricing/EnterpriseCallout";

const Pricing = () => {
  const {
    t
  } = useTranslation("pricing");
  const basicFeatures = [{
    id: "database-access",
    text: "Adgang til udbud via database",
    link: "/platform/agent"
  }, {
    id: "tender-agent",
    text: "Overvågning og notifikationer via udbudsagent",
    link: "/platform/agent"
  }, {
    id: "translations",
    text: "250 oversættelser pr. mdr.",
    link: "/platform/translations-analysis"
  }, {
    id: "analysis",
    text: "250 materiale- og udbudsanalyser pr. mdr.",
    link: "/platform/translations-analysis"
  }, {
    id: "search-tools",
    text: "Standard søge- og filtreringsværktøjer",
    link: "/platform/agent"
  }, {
    id: "legal-hotline",
    text: "Adgang til juridisk hotline",
    link: "/platform/legal-assistance"
  }, {
    id: "tech-support",
    text: "Teknisk support",
    link: "/support/technical-support"
  }];
  const premiumFeatures = [{
    id: "all-basic",
    text: "Alle Basis-funktioner",
    link: null
  }, {
    id: "legal-advice",
    text: "Adgang til udvidet juridisk hotline",
    link: "/platform/legal-assistance"
  }, {
    id: "translations-premium",
    text: "500 oversættelser pr. mdr.",
    link: "/platform/translations-analysis"
  }, {
    id: "analysis-premium",
    text: "500 materiale- og udbudsanalyser pr. mdr.",
    link: "/platform/translations-analysis"
  }, {
    id: "competitor-insights",
    text: "Konkurrent- og kontrakt-indsigt",
    link: "/platform/market-analysis-opportunities"
  }, {
    id: "contract-notifications",
    text: "Kontraktmuligheder og markedsanalyse",
    link: "/platform/market-analysis-opportunities"
  }];
  return <PageLayout hideHeader>
      <div className="bg-gradient-to-br from-purple-200 via-purple-100 to-indigo-200">
        <div className="pt-24 pb-10">
          <div className="container mx-auto px-4 max-w-7xl">
            <motion.div className="text-center mb-12" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }}>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                Priser
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Vælg den løsning, der passer til jeres behov
              </p>
              <div className="flex items-center justify-center mt-6 gap-2">
                <span className="text-sm font-medium">Alle priser er ekskl. moms</span>
              </div>
            </motion.div>

            {/* Pricing Cards */}
            <div className="flex flex-col md:flex-row gap-8 justify-center">
              {/* Basic Plan */}
              <motion.div className="bg-white rounded-lg shadow-md overflow-hidden flex-1 max-w-md border border-gray-100 flex flex-col" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }}>
                <div className="p-6 flex flex-col flex-grow">
                  <h2 className="text-2xl font-bold">Basis</h2>
                  <div className="mt-4 flex items-end">
                    <span className="text-4xl font-bold">12.000 kr</span>
                    <span className="ml-1 text-gray-500">pr. år</span>
                  </div>

                  <div className="mt-6 space-y-4 flex-grow">
                    {basicFeatures.map(feature => <div key={feature.id} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        {feature.link ? <Link to={feature.link} className="flex-none inline-flex items-center space-x-1 text-gray-700 hover:text-primary">
                            <span>{feature.text}</span>
                            <ExternalLink className="h-3.5 w-3.5 flex-none" />
                          </Link> : <span className="text-gray-700">{feature.text}</span>}
                      </div>)}
                  </div>

                  <div className="mt-8">
                    
                  </div>
                </div>
              </motion.div>

              {/* Premium Plan */}
              <motion.div className="bg-white rounded-lg shadow-md overflow-hidden flex-1 max-w-md border border-gray-100 flex flex-col" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }}>
                <div className="p-6 flex flex-col flex-grow">
                  <h2 className="text-2xl font-bold">Premium</h2>
                  <div className="mt-4 flex items-end">
                    <span className="text-4xl font-bold">22.000 kr</span>
                    <span className="ml-1 text-gray-500">pr. år</span>
                  </div>

                  <div className="mt-6 space-y-4 flex-grow">
                    {premiumFeatures.map(feature => <div key={feature.id} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        {feature.link ? <Link to={feature.link} className="flex-none inline-flex items-center space-x-1 text-gray-700 hover:text-primary">
                            <span>{feature.text}</span>
                            <ExternalLink className="h-3.5 w-3.5 flex-none" />
                          </Link> : <span className="text-gray-700">{feature.text}</span>}
                      </div>)}
                  </div>

                  <div className="mt-8">
                    
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Try Before You Buy Section */}
            <motion.div className="mt-16 bg-white rounded-lg shadow-md p-8 border border-gray-100 text-center" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                Prøv det først
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
                Vi tilbyder, at du kan prøve vores platform fuldstændig gratis, før du beslutter dig. 
                Kontakt os i dag, og få en gratis prøveperiode.
              </p>
              <Button size="lg" className="px-8">
                Kontakt os
              </Button>
            </motion.div>
            
            {/* Enterprise Plan */}
            <EnterpriseCallout />
          </div>
        </div>
      </div>
    </PageLayout>;
};
export default Pricing;
